<script setup lang="ts">
import { computed } from 'vue'
import DonationDetailPanel from '@/components/dashboard/modals/DonationDetailPanel.vue'
import RecurringDonationModal from '@/components/dashboard/modals/RecurringDonationModal.vue'
import { useManualGivingDonations } from '@/composables/useManualGivingDonations'
import {
  useDetailNavigation,
  type DetailFrame,
  type DonationDetailFrame,
} from '@/composables/useDetailNavigation'
import type { DonorDonation } from '@/types/donorFinancialYear'

const props = defineProps<{
  frame: DetailFrame
}>()

const emit = defineEmits<{
  busy: [busy: boolean]
  deleted: [donationId: string]
  updated: [donation: DonorDonation]
  openHistory: [donation: DonorDonation]
  seriesDeleted: [seriesId: string]
  labelledBy: [id: string]
}>()

const { stack } = useDetailNavigation()
const { manualDonations } = useManualGivingDonations()

const donationFrame = computed(() =>
  props.frame.kind === 'donation' ? props.frame : null
)

const displayedDonation = (frame: DonationDetailFrame) =>
  manualDonations.value.find((row) => row.id === frame.donation.id) ??
  frame.donation

const embedded = computed(() => {
  const frame = donationFrame.value
  if (!frame) return false
  const index = stack.value.findIndex((entry) => entry.key === frame.key)
  const parent = index > 0 ? stack.value[index - 1] : null
  return (
    parent?.kind === 'recurring' &&
    parent.seriesId === frame.donation.recurringSeriesId
  )
})
</script>

<template>
  <DonationDetailPanel
    v-if="donationFrame"
    :donation="displayedDonation(donationFrame)"
    :embedded="embedded"
    @busy="emit('busy', $event)"
    @deleted="emit('deleted', $event)"
    @updated="emit('updated', $event)"
  />
  <RecurringDonationModal
    v-else-if="frame.kind === 'recurring'"
    inline
    :series-id="frame.seriesId"
    @open-history="emit('openHistory', $event)"
    @series-deleted="emit('seriesDeleted', $event)"
    @busy="emit('busy', $event)"
    @labelled-by="emit('labelledBy', $event)"
  />
</template>
